import type { MarketingPermissions } from "@/hooks/usePermissions";
import type { NotificationSeverity, NotificationSource, NotificationType } from "@/types/marketing";
import type { LucideIcon } from "lucide-react";
import { BarChart3, Clapperboard, Megaphone, PenSquare, Server } from "lucide-react";

export type TonePreset = "hype" | "storytelling" | "direct_response" | "behind_the_scenes";

export type ShortFormTemplatePreset = {
  id: string;
  label: string;
  gradientClass: string;
  accent: string;
  description: string;
  overlayClass?: string;
};

export const SHORTFORM_TEMPLATES: ShortFormTemplatePreset[] = [
  {
    id: "clean",
    label: "Clean Minimal",
    gradientClass: "from-slate-950 via-slate-900 to-black",
    accent: "#38bdf8",
    description: "Neutral grid for text-first layouts.",
  },
  {
    id: "bold-gradient",
    label: "Bold Gradient",
    gradientClass: "from-fuchsia-600 via-rose-500 to-amber-400",
    accent: "#fef9c3",
    description: "High saturation hero gradient.",
  },
  {
    id: "psychedelic-fall",
    label: "Psychedelic Fall",
    gradientClass: "from-purple-900 via-orange-600 to-amber-300",
    accent: "#fef3c7",
    description: "Trippy grain and dual-tone text.",
    overlayClass: "bg-gradient-to-t from-transparent via-purple-500/20 to-orange-500/20",
  },
  {
    id: "warm-winds",
    label: "Warm Winds Desert",
    gradientClass: "from-amber-900 via-orange-800 to-yellow-500",
    accent: "#1c1917",
    description: "Dusty golden hour color wash.",
  },
  {
    id: "rustic",
    label: "Rustic Americana Textured",
    gradientClass: "from-stone-900 via-amber-800 to-red-900",
    accent: "#fcd34d",
    description: "Distressed overlay with serif callouts.",
    overlayClass: "bg-gradient-to-br from-white/5 via-transparent to-black/40",
  },
  {
    id: "black-gold",
    label: "Black & Gold High Contrast",
    gradientClass: "from-black via-slate-900 to-black",
    accent: "#fbbf24",
    description: "High-contrast luxe CTA focus.",
    overlayClass: "bg-gradient-to-b from-white/10 via-transparent to-black/70",
  },
];

export const SHORTFORM_FONTS = ["Clash Display", "Space Grotesk", "Sora", "Archivo Black"];

export const CTA_PRESETS = [
  { id: "listen", label: "Listen Now" },
  { id: "single", label: "New Single Out" },
  { id: "tickets", label: "Tickets Available" },
  { id: "watch", label: "Watch the Full Video" },
];

export const SHORTFORM_EFFECTS = [
  { id: "fade", label: "Fade-in" },
  { id: "slide", label: "Slide-up" },
  { id: "pulse", label: "Pulse" },
  { id: "glow", label: "Glow" },
];

export const SHORTFORM_FILTERS = [
  { id: "blur", label: "Blur" },
  { id: "vignette", label: "Vignette" },
  { id: "grain", label: "Grain" },
  { id: "warm", label: "Warm color cast" },
];

export const TONE_PRESETS: TonePreset[] = ["hype", "storytelling", "direct_response", "behind_the_scenes"];

export const toneLabels: Record<TonePreset, string> = {
  hype: "Hype",
  storytelling: "Storytelling",
  direct_response: "Direct Response",
  behind_the_scenes: "Behind-the-scenes",
};

export type PlatformKey = "instagram" | "tiktok" | "facebook" | "youtube_shorts";

export const PLATFORMS: PlatformKey[] = ["instagram", "tiktok", "facebook", "youtube_shorts"];

export const platformLabels: Record<PlatformKey, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  youtube_shorts: "YouTube Shorts",
};

export const PLATFORM_PERMISSION_MAP: Record<PlatformKey, keyof MarketingPermissions["platform"]> = {
  instagram: "instagram",
  tiktok: "tiktok",
  facebook: "facebook",
  youtube_shorts: "youtube",
};

export const SEVERITY_BADGE_STYLES: Record<NotificationSeverity, string> = {
  info: "bg-muted text-foreground/80",
  warning: "bg-amber-500/20 text-primary-foreground dark:text-amber-200 border border-amber-400/70",
  error: "bg-rose-500/20 text-rose-200 border border-rose-400/70",
  success: "bg-emerald-500/20 text-emerald-200 border border-emerald-400/60",
};

export const NOTIFICATION_TYPE_META: Record<NotificationType, { label: string; icon: LucideIcon }> = {
  clip: { label: "Clip", icon: Clapperboard },
  post: { label: "Post", icon: PenSquare },
  campaign: { label: "Campaign", icon: Megaphone },
  analytics: { label: "Analytics", icon: BarChart3 },
  system: { label: "System", icon: Server },
};

export const NOTIFICATION_SOURCE_LABELS: Record<NotificationSource, string> = {
  marketing: "Marketing",
  podcast: "Podcast",
  system: "System",
};

export const NOTIFICATION_SOURCE_FILTERS: Array<{ value: NotificationSource | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "podcast", label: "Podcast" },
  { value: "marketing", label: "Marketing" },
  { value: "system", label: "System" },
];
